<script setup>
import {
	computed,
	defineEmits,
	defineProps,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
} from 'vue';
import dayjs from 'dayjs';
import chevronLeft from '@/assets/images/icons/student-media/chevron-left.svg';
import chevronRight from '@/assets/images/icons/student-media/chevron-right.svg';
import classTeacherIcon from '@/assets/images/icons/student-media/class-teacher.svg';
import spoonIcon from '@/assets/images/icons/student-media/spoon.svg';
import chatIcon from '@/assets/images/icons/student-media/chat.svg';

const props = defineProps({
	images: {
		type: Array,
		required: true,
	},
	index: {
		type: Number,
		required: true,
	},
});

const emit = defineEmits(['close']);

const imgIndex = ref(0);

watch(
	() => props.index,
	(value) => {
		imgIndex.value = value >= 0 ? value : 0;
	},
	{ immediate: true }
);

const current = computed(() => props.images[imgIndex.value] || {});

const heading = computed(
	() => current.value.display_type || current.value.title || ''
);

const skill_name = computed(() => current.value.skill_name || '');

const detail = computed(() => {
	const title = current.value.title;
	if (title && title !== heading.value) return title;
	return current.value.note || '';
});

const datetime = computed(() => {
	if (current.value.display_datetime) return current.value.display_datetime;
	const value = current.value.date || current.value.inserted_at;
	return value ? dayjs(value).format('DD/MM/YYYY') : '';
});

const typeIcon = computed(() => {
	const label = `${current.value.type || ''} ${
		current.value.display_type || ''
	} ${current.value.title || ''}`
		.toLowerCase()
		.replace(/\s+/g, ' ');
	if (
		label.includes('phụ huynh gửi từ nhóm chat') ||
		label.includes('phu huynh gui tu nhom chat') ||
		label.includes('chat_medium') ||
		label.includes('nhóm chat') ||
		label.includes('nhom chat')
	) {
		return chatIcon;
	}
	if (
		label.includes('báo cáo bán trú') ||
		label.includes('bao cao ban tru')
	) {
		return spoonIcon;
	}
	if (
		label.includes('cá nhân') ||
		label.includes('ca nhan') ||
		label.includes('personal') ||
		label.includes('goal') ||
		label.includes('mục tiêu')
	) {
		return classTeacherIcon;
	}
	return '';
});

const canPrev = computed(() => imgIndex.value > 0);
const canNext = computed(() => imgIndex.value < props.images.length - 1);

const prev = () => {
	if (canPrev.value) imgIndex.value -= 1;
};

const next = () => {
	if (canNext.value) imgIndex.value += 1;
};

const onKeydown = (event) => {
	if (event.key === 'Escape') emit('close');
	if (event.key === 'ArrowLeft') prev();
	if (event.key === 'ArrowRight') next();
};

onMounted(() => {
	window.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
	window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
	<div class="media-overlay" @click="emit('close')">
		<div class="media-modal bg-white rounded-2xl" @click.stop>
			<div class="flex items-center justify-center gap-5">
				<button
					v-if="images.length > 1"
					type="button"
					class="media-nav flex-shrink-0"
					:class="{ 'media-nav--disabled': !canPrev }"
					:disabled="!canPrev"
					@click="prev"
				>
					<img :src="chevronLeft" alt="" width="24" height="24" />
				</button>
				<div class="flex flex-col gap-5 min-w-0">
					<div class="media-frame">
						<video
							v-if="current.asset_type === 'video'"
							:key="current.id || current.asset_url"
							controls
							:poster="current.video_poster_url || undefined"
							:src="current.asset_url"
						></video>
						<img
							v-else
							:key="current.id || current.asset_url"
							:src="current.asset_url"
							alt=""
						/>
					</div>
					<div class="flex items-start justify-between gap-4">
						<div class="flex flex-col gap-1 min-w-0">
							<div v-if="heading" class="flex items-center gap-2 mb-2">
								<img
									v-if="typeIcon"
									:src="typeIcon"
									alt=""
									width="20"
									height="20"
								/>
								<p class="media-title font-semibold truncate">
									{{ heading }}
								</p>
							</div>
							<div v-if="skill_name" class="mb-2">
								<div class="text-xs text-neutral-400">LĨNH VỰC</div>
								<div class=" text-neutral-600">{{ skill_name }}</div>
							</div>
							<div v-if="detail" >
								<div class="text-xs text-neutral-400">MỤC TIÊU</div>
								<div class=" text-neutral-600">{{ detail }}</div>
							</div>
							
						</div>
						<p v-if="datetime" class="media-meta">
							{{ datetime }}
						</p>
					</div>
				</div>
				<button
					v-if="images.length > 1"
					type="button"
					class="media-nav flex-shrink-0"
					:class="{ 'media-nav--disabled': !canNext }"
					:disabled="!canNext"
					@click="next"
				>
					<img :src="chevronRight" alt="" width="24" height="24" />
				</button>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.media-overlay {
	position: fixed;
	inset: 0;
	z-index: 1000000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(107, 114, 128, 0.75);
}

.media-modal {
	max-width: calc(100vw - 48px);
	padding: 48px;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.1),
		0 20px 24px rgba(20, 37, 63, 0.06);
}

.media-nav {
	padding: 12px;
	border: 1px solid #e5a42b;
	border-radius: 12px;
	line-height: 0;
	cursor: pointer;
}

.media-nav--disabled {
	opacity: 0.4;
	cursor: default;
}

.media-frame {
	width: #{'min(806px, calc(100vw - 280px))'};

	img,
	video {
		display: block;
		width: 100%;
		max-height: calc(100vh - 320px);
		height: auto;
		border-radius: 8px;
		object-fit: contain;
		background: #fff;
	}
}

.media-title {
	color: #48566a;
	font-size: 19px;
	line-height: 24px;
	letter-spacing: -0.38px;
}

.media-meta,
.media-count {
	color: #344256;
	font-size: 16px;
	line-height: 24px;
}

.media-count {
	color: #48566a;
}
</style>
